import { DateTime } from "luxon";

import { Field, TAnswers } from "src/shared/contexts/HackerContext/types";
import { FindMyBusesByEventQuery } from "src/views/hacker/rsvp/travel-form/graphql/findMyBusesByEvent.generated";
import { getReimbursementAmount } from "src/views/hacker/rsvp/travel/ReimbursementAmount";

const EVENT_TIMEZONE = "America/Toronto";
const EVENT_STOPS = ["University of Waterloo", "Lazaridis Hall"];

type TravelContextValue = string | number | boolean;

export type TravelContextPayload = {
  [key: string]: TravelContextValue | undefined;
};

export type TravelContextEntry = {
  key: string;
  label: string;
  value: string;
};

type TravelContextUser = {
  displayName?: string | null;
  email?: string | null;
};

type RegisteredBus = FindMyBusesByEventQuery["findMyBusesByEvent"][number];

const TRAVEL_METHOD_LABELS: Record<string, string> = {
  flight: "Flight",
  bus: "Hack the North provided bus",
  drive: "Drive",
  walk: "Walk",
  other: "Other",
};

const REIMBURSEMENT_METHOD_LABELS: Record<string, string> = {
  paypal: "PayPal",
  cadETransfer: "Interac e-Transfer",
};

const CONTEXT_FIELDS: Array<{
  key: string;
  label: string;
  format?: (value: TravelContextValue) => string;
}> = [
  { key: "display_name", label: "Display name" },
  { key: "email", label: "Email" },
  { key: "origin_location", label: "Travelling from" },
  { key: "travel_method", label: "Travel method" },
  { key: "travel_plan_submitted", label: "Travel plan submitted" },
  { key: "inbound_arrival_airport", label: "Arrival airport" },
  { key: "inbound_flight_number", label: "Arrival flight number" },
  { key: "inbound_airline", label: "Arrival airline" },
  { key: "inbound_arrival_time", label: "Arrival date and time (EDT)" },
  { key: "outbound_departure_airport", label: "Departure airport" },
  { key: "outbound_flight_number", label: "Departure flight number" },
  { key: "outbound_airline", label: "Departure airline" },
  { key: "outbound_departure_time", label: "Departure date and time (EDT)" },
  { key: "flight_cost_amount", label: "Submitted flight cost" },
  { key: "flight_cost_currency", label: "Flight cost currency" },
  { key: "flight_receipt_uploaded", label: "Flight receipt uploaded" },
  { key: "bus_to_event_route", label: "Bus to event route" },
  { key: "bus_to_event_start", label: "Bus pickup location" },
  { key: "bus_to_event_start_address", label: "Bus pickup address" },
  { key: "bus_to_event_departure", label: "Bus pickup time" },
  { key: "bus_to_event_end", label: "Bus arrival destination" },
  { key: "bus_to_event_end_address", label: "Bus arrival address" },
  { key: "bus_to_event_arrival", label: "Bus expected arrival time" },
  { key: "bus_from_event_route", label: "Bus from event route" },
  { key: "bus_from_event_start", label: "Return bus pickup location" },
  { key: "bus_from_event_start_address", label: "Return bus pickup address" },
  { key: "bus_from_event_departure", label: "Return bus pickup time" },
  { key: "bus_from_event_end", label: "Return bus destination" },
  {
    key: "bus_from_event_end_address",
    label: "Return bus destination address",
  },
  { key: "bus_from_event_arrival", label: "Return bus expected arrival time" },
  {
    key: "bus_coordinator_interest",
    label: "Interested in being a bus coordinator",
  },
  {
    key: "flight_reimbursement_eligible",
    label: "Flight reimbursement eligible",
  },
  {
    key: "flight_reimbursement_limit_cad",
    label: "Flight reimbursement limit",
    format: (value) => `CAD $${value}`,
  },
  { key: "flight_reimbursement_status", label: "Flight reimbursement status" },
  { key: "reimbursement_method", label: "Reimbursement method" },
  {
    key: "reimbursement_payment_details_provided",
    label: "Reimbursement payment details provided",
  },
  {
    key: "rapid_contact_information_provided",
    label: "Rapid contact information provided",
  },
  {
    key: "requires_visa",
    label: "Requires a visa",
  },
  {
    key: "visa_tracking_number_provided",
    label: "Visa tracking number provided",
  },
  {
    key: "entry_eligibility_acknowledged",
    label: "Entry eligibility acknowledged",
  },
  {
    key: "visa_guidelines_acknowledged",
    label: "Visa guidelines acknowledged",
  },
  {
    key: "reimbursement_rules_acknowledged",
    label: "Reimbursement rules acknowledged",
  },
];

const nonBlankString = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value.trim() : undefined;

const booleanValue = (value: unknown): boolean | undefined =>
  typeof value === "boolean" ? value : undefined;

const numberValue = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined;

const hasUploadedReceipt = (value: unknown): boolean => {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "string") return value.trim().length > 0;
  return typeof value === "object" && value !== null;
};

const formatDateTime = (value: string | undefined): string | undefined => {
  if (!value) return undefined;

  const dateTime = DateTime.fromISO(value, { setZone: true });
  if (!dateTime.isValid) return value;

  return dateTime
    .setZone(EVENT_TIMEZONE)
    .toFormat("ccc, LLL d, yyyy 'at' h:mm a ZZZZ");
};

const getBusContext = (buses: RegisteredBus[]): TravelContextPayload => {
  const context: TravelContextPayload = {};

  buses.forEach((bus) => {
    const stops = [...(bus.stops ?? [])].sort((a, b) =>
      a.departure_time.localeCompare(b.departure_time)
    );
    const firstStop = stops[0];
    const lastStop = stops[stops.length - 1];
    if (!firstStop || !lastStop) return;

    const route = stops.map((stop) => stop.name).join(" → ");
    const isToEvent = EVENT_STOPS.includes(lastStop.name);
    const isFromEvent = EVENT_STOPS.includes(firstStop.name);

    if (isToEvent) {
      context.bus_to_event_route = route;
      context.bus_to_event_start = firstStop.name;
      context.bus_to_event_start_address = nonBlankString(firstStop.address);
      context.bus_to_event_departure = formatDateTime(firstStop.departure_time);
      context.bus_to_event_end = lastStop.name;
      context.bus_to_event_end_address = nonBlankString(lastStop.address);
      context.bus_to_event_arrival = formatDateTime(lastStop.departure_time);
    } else if (isFromEvent) {
      context.bus_from_event_route = route;
      context.bus_from_event_start = firstStop.name;
      context.bus_from_event_start_address = nonBlankString(firstStop.address);
      context.bus_from_event_departure = formatDateTime(
        firstStop.departure_time
      );
      context.bus_from_event_end = lastStop.name;
      context.bus_from_event_end_address = nonBlankString(lastStop.address);
      context.bus_from_event_arrival = formatDateTime(lastStop.departure_time);
    }
  });

  return context;
};

export const buildTravelContext = (
  claimData: Partial<TAnswers> | undefined,
  buses: RegisteredBus[] = [],
  user: TravelContextUser = {}
): TravelContextPayload => {
  const userContext: TravelContextPayload = {
    display_name: nonBlankString(user.displayName),
    email: nonBlankString(user.email),
  };

  if (!claimData) return { ...userContext, ...getBusContext(buses) };

  const originLocation =
    nonBlankString(claimData[Field.LOCATION]) ??
    nonBlankString(claimData[Field.SHORT_LOCATION]);
  const travelMethod = nonBlankString(claimData[Field.TRAVEL_OPTION]);
  const receiptUploaded = hasUploadedReceipt(claimData[Field.TRAVEL_RECEIPT]);
  const rapidContactInformationProvided = Boolean(
    nonBlankString(claimData[Field.TRAVEL_PHONE_NUMBER])
  );
  const reimbursementPaymentDetailsProvided = [
    Field.REIMBURSEMENT_RECIPIENT_NAME,
    Field.REIMBURSEMENT_RECIPIENT_ADDRESS,
    Field.REIMBURSEMENT_RECIPIENT_PAYPAL,
    Field.REIMBURSEMENT_RECIPIENT_ETRANSFER,
  ].some((field) => Boolean(nonBlankString(claimData[field])));
  const travelPlanSubmitted =
    travelMethod === "flight"
      ? receiptUploaded ||
        Boolean(nonBlankString(claimData[Field.REIMBURSEMENT_RECIPIENT_NAME]))
      : travelMethod === "bus"
      ? rapidContactInformationProvided
      : false;
  const context: TravelContextPayload = {
    travel_plan_submitted: travelPlanSubmitted,
    origin_location: originLocation,
    travel_method: travelMethod
      ? TRAVEL_METHOD_LABELS[travelMethod] ?? travelMethod
      : undefined,
    rapid_contact_information_provided: rapidContactInformationProvided,
    bus_coordinator_interest: booleanValue(
      claimData[Field.BUS_COORDINATOR_INTEREST]
    ),
    requires_visa: booleanValue(claimData[Field.REQUIRES_VISA]),
    entry_eligibility_acknowledged: booleanValue(
      claimData[Field.ENTRY_ELIGIBILITY_ACKNOWLEDGEMENT]
    ),
    visa_guidelines_acknowledged: booleanValue(
      claimData[Field.VISA_ACKNOWLEDGEMENT]
    ),
    visa_tracking_number_provided: Boolean(
      nonBlankString(claimData[Field.VISA_TRACKING])
    ),
  };

  if (travelMethod === "flight") {
    const reimbursementLimit = getReimbursementAmount(originLocation).amount;
    const flightCost = numberValue(claimData[Field.FLIGHT_AMOUNT]);
    const reimbursementMethod = nonBlankString(
      claimData[Field.REIMBURSEMENT_METHOD]
    );

    context.flight_reimbursement_eligible = reimbursementLimit > 0;
    context.flight_reimbursement_limit_cad =
      reimbursementLimit > 0 ? reimbursementLimit : undefined;
    context.flight_cost_amount = flightCost;
    context.flight_cost_currency =
      flightCost !== undefined
        ? nonBlankString(claimData[Field.FLIGHT_CURRENCY])
        : undefined;
    context.flight_receipt_uploaded = receiptUploaded;
    context.flight_reimbursement_status = nonBlankString(
      claimData[Field.FLIGHT_REIMBURSEMENT]
    );
    context.reimbursement_method = reimbursementMethod
      ? REIMBURSEMENT_METHOD_LABELS[reimbursementMethod] ?? reimbursementMethod
      : undefined;
    context.reimbursement_payment_details_provided =
      reimbursementPaymentDetailsProvided;
    context.reimbursement_rules_acknowledged = booleanValue(
      claimData[Field.REIMBURSEMENT_ACKNOWLEDGEMENT]
    );

    context.inbound_airline = nonBlankString(
      claimData[Field.FRIDAY_FLIGHT_AIRLINE]
    );
    context.inbound_flight_number = nonBlankString(
      claimData[Field.FRIDAY_FLIGHT_NUMBER]
    );
    context.inbound_arrival_airport = nonBlankString(
      claimData[Field.ARRIVAL_AIRPORT]
    );
    context.inbound_arrival_time = formatDateTime(
      nonBlankString(claimData[Field.FRIDAY_FLIGHT_ARRIVAL_TIME])
    );
    context.outbound_airline = nonBlankString(
      claimData[Field.SUNDAY_FLIGHT_AIRLINE]
    );
    context.outbound_flight_number = nonBlankString(
      claimData[Field.SUNDAY_FLIGHT_NUMBER]
    );
    context.outbound_departure_airport = nonBlankString(
      claimData[Field.DEPARTURE_AIRPORT]
    );
    context.outbound_departure_time = formatDateTime(
      nonBlankString(claimData[Field.SUNDAY_FLIGHT_DEPARTURE_TIME])
    );
  }

  return { ...userContext, ...context, ...getBusContext(buses) };
};

const formatContextValue = (value: TravelContextValue): string =>
  typeof value === "boolean" ? (value ? "Yes" : "No") : String(value);

export const getTravelContextEntries = (
  context: TravelContextPayload
): TravelContextEntry[] =>
  CONTEXT_FIELDS.reduce<TravelContextEntry[]>((entries, field) => {
    const value = context[field.key];
    if (value === undefined) return entries;

    entries.push({
      key: field.key,
      label: field.label,
      value: field.format ? field.format(value) : formatContextValue(value),
    });
    return entries;
  }, []);
